import { useState, useMemo, useEffect, useRef } from 'react'
import { ELiteDashboardHead } from '../components/elite/dashboard/EliteDashboardHead'
import { useSearchParams } from 'react-router-dom'
import { onReady, trackEvent, TrackEventPayload } from '../utils/appBridge'
import { throttle } from '../utils/utils'
import { EliteOnboardingStatusResponse, EventNames } from '../types/misc.types'
import { fetchOnboardingAdvisoryStatus } from '../repository/misc'
import { useQuery } from '@tanstack/react-query'
import { EliteDashboardBody } from '../components/elite/dashboard/EliteDashboardBody'
import { EliteLandingPageLoader } from '../components/elite/landing-page/EliteLandingPageLoader'
import { ViewAllBenefitsBottomSheet } from '../components/BottomSheetComponents/ViewAllBenefitsBottomSheet'
import { useBottomSheet } from '../state-management/contexts/BottomSheetContext'
import { ElitePageBackBar } from '../components/elite/ElitePageBackBar'
import { getEliteBuyCtaEventExtras } from '../utils/eliteEventExtras'
import { EliteBuyCtaExtrasProvider } from '../components/elite/EliteBuyCtaExtrasContext'

export const EliteDashboardPage = () => {
  const [isEliteButtonVisible, setIsEliteButtonVisible] = useState(false)

  const [eliteDashboardData, setEliteDashboardData] = useState<EliteOnboardingStatusResponse>()
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const onReadyLock = useRef<boolean>(false)

  const throttledEliteButtonVisibility = throttle((eliteButtonVisibility: boolean) => {
    setIsEliteButtonVisible(eliteButtonVisibility)
  }, 0)
  const handleEliteButtonVisibility = (eliteButtonVisibility: boolean) =>
    throttledEliteButtonVisibility(eliteButtonVisibility)

  const { data, isSuccess, isLoading, isError } = useQuery<any, Error>({
    queryKey: ['ONBOARDING_ADVISORY_STATUS'],
    queryFn: fetchOnboardingAdvisoryStatus,
    retry: 1,
    refetchOnWindowFocus: false
  })

  useEffect(() => {
    if (isSuccess) {
      const dataCopy = data?.data as EliteOnboardingStatusResponse
      setEliteDashboardData(() => dataCopy)
    }
  }, [isSuccess])

  const [searchParams] = useSearchParams()
  const eliteOnboardingStatus = (data?.data ?? eliteDashboardData) as EliteOnboardingStatusResponse | undefined
  const buyCtaExtras = getEliteBuyCtaEventExtras(eliteOnboardingStatus)
  const { amount, cta_name } = buyCtaExtras

  const viewBenefits = useMemo(() => {
    return searchParams?.get('viewBenefits') ?? 'false'
  }, [searchParams])

  const handleOpenViewAllBenefitsBottomSheet = () => {
    openBottomSheet(
      // Sheet renders in a portal (no context). Pass the same buy-CTA extras as a prop
      // so its inner AlsoUnlockEliteScores tap carries amount + plan ids.
      <ViewAllBenefitsBottomSheet
        onDismiss={() => {
          trackEvent({
            eventName: EventNames.ButtonTap,
            payload: {
              amount,
              cta_name,
              buttonLabel: 'Close'
            }
          })
          closeBottomSheet()
        }}
        ScreenName='MF_L2_Elite_Dashboard_Bottomsheet'
        buyCtaExtras={buyCtaExtras}
      />
    )
  }

  useEffect(() => {
    if (viewBenefits === 'true') {
      handleOpenViewAllBenefitsBottomSheet()
    }
  }, [viewBenefits])

  const handlePageReady = () => {
    if (!onReadyLock.current) {
      onReadyLock.current = true
      onReady()
    }

    const eventPayload: TrackEventPayload = {
      screenType: 'MF_Elite_Dashboard',
      amount,
      cta_name
    }

    if (data?.data?.renewal) {
      eventPayload.eventId = 'Renew'
    }

    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: eventPayload
    })
  }

  useEffect(() => {
    setTimeout(() => {
      handlePageReady()
    }, 1000)


  }, [])

  useEffect(() => {
    if (isSuccess || isError) handlePageReady()
  }, [isSuccess, isError])

  const onBGRiveLoad = () => { }

  if (isLoading) return <EliteLandingPageLoader />

  return (
    <EliteBuyCtaExtrasProvider status={eliteOnboardingStatus}>
    <div className={`h-screen flex flex-col relative bg-[#010101] w-full`}>
      <ElitePageBackBar isEliteButtonVisible={isEliteButtonVisible} />
      <div className='flex flex-1 h-1 w-full'>
        <div className='h-full overflow-y-scroll no-scrollbar overflow-x-hidden fixed top-0 overscroll-none w-full'>
          <div className='flex flex-col h-max w-full relative z-0'>
            <ELiteDashboardHead
              handleEliteButtonVisibility={handleEliteButtonVisibility}
              membershipValidity={eliteDashboardData?.userSubscriptionDetails?.subscriptionEndTime ?? 0}
              renewal={eliteDashboardData?.renewal ?? undefined}
              onBGRiveLoad={onBGRiveLoad}
              onViewAllBenefitsClick={handleOpenViewAllBenefitsBottomSheet}
            />
            <div className='z-[1] rounded-xl'>
              <EliteDashboardBody
                cancelMembershipCTA={eliteDashboardData?.cancelSubscriptionCta}
                eliteOnboardingStatus={eliteDashboardData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    </EliteBuyCtaExtrasProvider>
  )
}